import type { Listing } from '@adc/sdk/types'
import React, { createContext, useContext, ReactNode } from 'react'
import * as blueprint from '@adc/blueprint'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
  PDPListingType,
} from '@adc/blueprint'

// the listing context here is created so we don't need to prop drill segment and listing through all the components,
// use the listing context here within functional components that use them and at a certain point we can stop passing them down through props,
// an example can be seen in bidBanner/index.tsx

export type CombinedListingType =
  AuctionBlueprintOnlineReactiveArgs['listing'] & PDPListingType

// this should always match the segment type emitted from this context
export type ShortenedOnlineSegmentType =
  AuctionBlueprintScheduledOnlineReactiveArgs['segment']

export type ListingPageActionsType = ReturnType<
  typeof blueprint.listing.hooks.usePage
>['actions']

const ListingContext = createContext<
  | {
      segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
      listing: CombinedListingType
      listingActions?: ListingPageActionsType
      activeListings?: Listing[]
    }
  | undefined
>(undefined)

export const useListingContext = () => {
  const context = useContext(ListingContext)
  if (context === undefined) {
    throw new Error(
      'useListingContext must be used within a ListingContextProvider'
    )
  }
  return context
}

interface ListingProviderProps {
  children: ReactNode
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  listing: CombinedListingType
  listingActions?: ListingPageActionsType
  activeListings?: Listing[]
}

export const ListingProvider = ({
  children,
  listing,
  segment,
  listingActions,
  activeListings,
}: ListingProviderProps) => (
  <ListingContext.Provider
    value={{ listing, segment, listingActions, activeListings }}
  >
    {children}
  </ListingContext.Provider>
)

export default ListingProvider
